import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { useBookingContext } from '../BookingContext'
import { Button } from '@w/components/ui/button'
import { Input } from '@w/components/ui/input'
import { Label } from '@w/components/ui/label'
import { Card, CardContent } from '@w/components/ui/card'
import { Badge } from '@w/components/ui/badge'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@w/components/ui/form'
import ServiceSelection from '../components/ServiceSelection'
import ServiceCard from '../components/ServiceCard'
import { MapPin, ArrowRight, Sparkles, Clock, DollarSign } from 'lucide-react'
import CustomQuestions, { CustomQuestion } from '../components/CustomQuestions'
import FieldNodeWrapper from '../components/inline-editor/FieldNodeWrapper'

export default function ServiceSelectionStep() {
  const {
    config,
    services,
    selectedService,
    serviceAddress,
    customerInfo,
    setAddress,
    setCustomerInfo,
    goToStep,
    configurationMode,
    // Inline editor state & actions
    selectedFieldId,
    selectField,
  } = useBookingContext()

  const fieldLabels = config.widgetConfig.field_labels || {}
  const customQuestions: CustomQuestion[] = config.widgetConfig.custom_questions || []

  // Check if address field should be visible
  const showAddress = config.widgetConfig.show_address !== false

  // Configuration mode: Show static form structure
  if (configurationMode) {
    return (
      <div className="max-w-2xl mx-auto space-y-8">
        {/* Service Address - Static Display (always shown in config, styled as hidden when disabled) */}
        <FieldNodeWrapper
          fieldId="address"
          isSelected={selectedFieldId === 'address'}
          onSelect={selectField}
        >
          <Card className={showAddress ? '' : 'opacity-50 bg-muted'}>
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="w-5 h-5 text-primary" />
                <Label className="text-xl font-semibold">
                  Service Address <span className="text-destructive">*</span>
                </Label>
                {!showAddress && (
                  <Badge variant="secondary" className="ml-auto">Hidden</Badge>
                )}
              </div>
              <div className="flex h-12 w-full rounded-md border border-input bg-background px-3 py-2 text-base cursor-pointer items-center">
                <span className="text-muted-foreground">{fieldLabels.address_placeholder || 'Enter your service address...'}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Where should we provide the service?
              </p>
            </CardContent>
          </Card>
        </FieldNodeWrapper>

        {/* Service Selection - Static display (always show list view in config) */}
        <Card className="border-none shadow-none">
          <CardContent className="p-6">
            <div className="mb-6">
              <h3 className="font-semibold text-xl mb-1 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                Choose a Service
              </h3>
              <p className="text-sm text-muted-foreground">Select the service you'd like to book</p>
            </div>

            {services.length > 0 ? (
              <div className="space-y-3 max-h-[450px] overflow-y-auto pr-1">
                {services.map((service) => {
                  // Format duration
                  const hours = Math.floor(service.base_duration / 60)
                  const minutes = service.base_duration % 60
                  const durationText = `${hours}h${minutes > 0 ? ` ${minutes}m` : ''}`

                  // Format price
                  let priceText = ''
                  if (service.price_range?.minimum) {
                    priceText = service.price_range.minimum
                  }

                  return (
                    <Card
                      key={service.id}
                      className="cursor-default opacity-90"
                    >
                      <CardContent className="p-5 flex gap-4">
                        {/* Icon */}
                        <div
                          className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{
                            backgroundColor: service.color_code
                              ? `${service.color_code}15`
                              : 'rgba(59, 130, 246, 0.1)',
                          }}
                        >
                          <svg
                            className="w-7 h-7"
                            style={{ color: service.color_code || '#3b82f6' }}
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                            />
                          </svg>
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-lg mb-2 text-foreground">
                            {service.name}
                          </h4>
                          {service.description && (
                            <p className="text-sm text-muted-foreground mb-3 line-clamp-2 leading-relaxed">
                              {service.description}
                            </p>
                          )}
                          <div className="flex gap-3 flex-wrap">
                            <Badge variant="secondary" className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {durationText}
                            </Badge>
                            {priceText && (
                              <Badge variant="outline" className="flex items-center gap-1 text-green-600 border-green-200">
                                <DollarSign className="w-3 h-3" />
                                From ${priceText}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-sm text-muted-foreground">No services configured</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Custom Questions - Static display */}
        {customQuestions.some((q) => q.show_on_step === 'service') && (
          <Card>
            <CardContent className="p-6 space-y-4">
              {customQuestions
                .filter(q => q.show_on_step === 'service')
                .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
                .map(question => (
                  <div key={question.id} className="space-y-2">
                    <Label>
                      {question.label} {question.required && <span className="text-destructive">*</span>}
                    </Label>
                    {question.type === 'text' && (
                      <Input disabled placeholder={question.placeholder} />
                    )}
                    {question.type === 'textarea' && (
                      <Input disabled placeholder={question.placeholder} />
                    )}
                    {question.help_text && (
                      <p className="text-xs text-muted-foreground">{question.help_text}</p>
                    )}
                  </div>
                ))}
            </CardContent>
          </Card>
        )}

        {/* Continue Button - Disabled */}
        <div className="flex justify-end pt-6 border-t">
          <Button
            type="button"
            disabled
            size="lg"
            className="gap-2 px-8"
          >
            Continue to Date & Time
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    )
  }

  // Build dynamic schema with custom questions for service step
  const serviceSchema = useMemo(() => {
    const baseSchema = showAddress
      ? { address: z.string().min(1, 'Service address is required') }
      : { address: z.string().optional() }

    // Add custom fields to schema
    const customFieldsSchema: Record<string, z.ZodTypeAny> = {}

    customQuestions
      .filter((q) => q.show_on_step === 'service')
      .forEach((question) => {
        if (question.type === 'checkbox') {
          customFieldsSchema[question.id] = question.required
            ? z.boolean().refine((val) => val === true, { message: `${question.label} is required` })
            : z.boolean().optional()
        } else {
          let field = z.string()
          if (question.validation?.min_length) {
            field = field.min(question.validation.min_length)
          }
          if (question.validation?.max_length) {
            field = field.max(question.validation.max_length)
          }
          customFieldsSchema[question.id] = question.required
            ? field.min(1, `${question.label} is required`)
            : field.optional()
        }
      })

    return z.object({
      ...baseSchema,
      customFields: z.object(customFieldsSchema).optional(),
    })
  }, [customQuestions, showAddress])

  type ServiceFormData = z.infer<typeof serviceSchema>

  const form = useForm<ServiceFormData>({
    resolver: zodResolver(serviceSchema),
    defaultValues: {
      address: serviceAddress || '',
      customFields: customerInfo?.customFields || {},
    },
  })

  const canContinue = (showAddress ? form.watch('address').trim() : true) && selectedService

  const handleContinue = form.handleSubmit((data) => {
    setAddress(data.address)
    // Merge any custom fields from this step with existing customerInfo
    if (data.customFields && Object.keys(data.customFields).length > 0) {
      setCustomerInfo({
        ...(customerInfo || { name: '', email: '', phone: '', smsConsent: false }),
        customFields: {
          ...(customerInfo?.customFields || {}),
          ...data.customFields,
        },
      })
    }
    goToStep('datetime')
  })

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <Form {...form}>
        <form onSubmit={handleContinue} className="space-y-8">
          {/* Service Address */}
          {showAddress && (
            <Card>
              <CardContent className="p-6">
                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-2 mb-4">
                        <MapPin className="w-5 h-5 text-primary" />
                        <FormLabel className="text-xl font-semibold">
                          {fieldLabels.address_label || 'Service Address'} <span className="text-destructive">*</span>
                        </FormLabel>
                      </div>
                      <FormControl>
                        <Input
                          type="text"
                          placeholder={fieldLabels.address_placeholder || 'Enter your service address...'}
                          className="text-base h-12"
                          {...field}
                        />
                      </FormControl>
                      <p className="text-xs text-muted-foreground mt-2">
                        Where should we provide the service?
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
          )}

          {/* Service Selection */}
          <div>
            {!selectedService && services.length > 1 ? (
              <ServiceSelection />
            ) : selectedService ? (
              <ServiceCard />
            ) : (
              <Card>
                <CardContent className="p-8 text-center">
                  <div className="animate-pulse space-y-3">
                    <div className="h-4 bg-muted rounded w-3/4 mx-auto"></div>
                    <div className="h-4 bg-muted rounded w-1/2 mx-auto"></div>
                  </div>
                  <p className="text-sm text-muted-foreground mt-4">Loading services...</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Custom Questions for Service Step */}
          {customQuestions.some((q) => q.show_on_step === 'service') && (
            <Card>
              <CardContent className="p-6">
                <CustomQuestions
                  questions={customQuestions}
                  control={form.control}
                  step="service"
                />
              </CardContent>
            </Card>
          )}

          {/* Continue Button */}
          <div className="flex justify-end pt-6 border-t">
            <Button
              type="submit"
              disabled={!canContinue}
              size="lg"
              className="gap-2 px-8"
            >
              Continue to Date & Time
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
